import { ViewChild } from '@angular/core';
import { Component, Input, OnInit } from '@angular/core';
import { Guid } from 'guid-typescript';
import { combineLatest } from 'rxjs';
import { DataInfo, discardChanges, EntriesCollection, Product, remove, saveChanges, TotalCostsService } from '../shared';
import { CsLocationSetup, GridConnectionSection, GridProvider, InstallationActivity, InstallationDescription, InstallationSection } from './service/cs-installation-section.model';
import { CsInstallationSectionService } from './service/cs-installation-section.service';

@Component({
  selector: 'app-cs-installation-section',
  templateUrl: './cs-installation-section.component.html',
  styleUrls: ['./cs-installation-section.component.scss']
})
export class CsInstallationSectionComponent implements OnInit {
  @Input() projectId: Guid;
  @ViewChild('csGridConnectionTable') public csGridConnectionTable: { startEdit: () => void; };
  @ViewChild('installationTable') public installationTable: { startEdit: () => void; };
  @ViewChild('installationDescriptionComponent') public installationDescriptionComponent: { startEdit: () => void; };
  gridProviders: ReadonlyArray<GridProvider>;
  selectedGridProvider: GridProvider;
  installationActivities: ReadonlyArray<InstallationActivity>;
  private csLocationSetupInfo: DataInfo<CsLocationSetup>;
  private gridConnectionSectionInfo: DataInfo<GridConnectionSection>;
  private installationDescriptionInfo: DataInfo<InstallationDescription>;
  private installationSectionInfo: DataInfo<InstallationSection>;
  private internalIsBusy: boolean;
  private internalSupraregionalInstallation: boolean;
  private internalInstallationActivitiesFilter: (product: Product) => boolean;

  get isBusy(): boolean {
    return this.internalIsBusy;
  }

  get hasCsLocationSetup(): boolean {
    return !!this.csLocationSetupInfo.data;
  }

  get csLocationSetup(): CsLocationSetup {
    return this.csLocationSetupInfo.data;
  }

  get hasGridConnectionSection(): boolean {
    return !!this.gridConnectionSectionInfo.data;
  }

  get gridConnectionSection(): GridConnectionSection {
    return this.gridConnectionSectionInfo.data;
  }

  get hasInstallationDescription(): boolean {
    return !!this.installationDescriptionInfo.data;
  }

  get installationDescription(): InstallationDescription {
    return this.installationDescriptionInfo.data;
  }

  get hasInstallationSection(): boolean {
    return !!this.installationSectionInfo.data;
  }

  get installationSection(): InstallationSection {
    return this.installationSectionInfo.data;
  }

  get gridProviderWasDeleted(): boolean {
    return !this.selectedGridProvider;
  }

  get supraregionalInstallation(): boolean {
    return this.internalSupraregionalInstallation;
  }

  get installationActivitiesFilter(): (product: Product) => boolean {
    return this.internalInstallationActivitiesFilter;
  }

  constructor(
    private service: CsInstallationSectionService,
    private totalCostsService: TotalCostsService) {
    this.csLocationSetupInfo = {data: undefined, isNew: false };
    this.gridConnectionSectionInfo = {data: undefined, isNew: false };
    this.installationSectionInfo = {data: undefined, isNew: false };
    this.installationDescriptionInfo = {data: undefined, isNew: false };
    this.service.isBusy$.subscribe(isBusy => this.internalIsBusy = isBusy);
  }

  ngOnInit(): void {
    this.service.getInstallationDescription(this.projectId)
      .subscribe(data => this.installationDescriptionInfo.data = data);

    this.service.getCsLocationSetup(this.projectId)
      .subscribe(data => this.csLocationSetupInfo.data = data);

    const gridProvider$ = this.service.getGridProviders();
    const gridConnectionSection$ = this.service.getGridConnectionSection(this.projectId);
    combineLatest([gridProvider$, gridConnectionSection$])
      .subscribe(([gridProviders, gridConnectionSection]) => {
        this.gridProviders = gridProviders;
        if (gridConnectionSection) {
          this.selectedGridProvider = this.gridProviders.find(gp => gp.id === gridConnectionSection.gridProviderId);
        }
        this.gridConnectionSectionInfo.data = gridConnectionSection;
        this.totalCostsService.sectionInitialized('gridConnection');
      });

    const installationActivities$ = this.service.getInstallationActivities();
    const installationSection$ = this.service.getInstallationSection(this.projectId);
    combineLatest([installationActivities$, installationSection$])
      .subscribe(([installationActivities, installationSection]) => {
        this.installationActivities = installationActivities;
        this.installationSectionInfo.data = installationSection;
        this.supraregionalInstallationChecked(installationSection?.supraregional);
        this.totalCostsService.sectionInitialized('csInstallation');
      });
  }

  initializeCsLocationSetup(elementId: string): void {
    this.csLocationSetupInfo.isNew = true;
    this.csLocationSetupInfo.data = {
      id: Guid.create(),
      backgroundImage: undefined,
      drawing: undefined,
      legendItems: undefined
    };
    this.gotoElement(elementId);
  }

  gotoElement(elementId: string): void {
    const element = document.getElementById(elementId);
    element.scrollIntoView({block: 'start', behavior: 'smooth'});
  }

  saveCsLocationSetupChanges(updatedCsLocationSetup: CsLocationSetup): void {
    saveChanges(this.projectId, updatedCsLocationSetup, this.csLocationSetupInfo,
      this.service.addCsLocationSetup.bind(this.service),
      this.service.updateCsLocationSetup.bind(this.service));
  }

  discardCsLocationSetupChanges(): void {
    discardChanges(this.csLocationSetupInfo);
  }

  deleteCsLocationSetup(): void {
    remove(this.projectId, this.csLocationSetupInfo,
      this.service.deleteCsLocationSetup.bind(this.service));
  }

  initializeGridConnectionSection(elementId: string): void {
    this.gridConnectionSectionInfo.isNew = true;
    this.selectedGridProvider = this.gridProviders[0];
    const sectionId = Guid.create();
    const createdOn = new Date();
    this.gridConnectionSectionInfo.data = createEmptyGridConnectionSection(sectionId, this.selectedGridProvider, createdOn);
    this.csGridConnectionTable.startEdit();
    this.gotoElement(elementId);
  }

  discardGridConnectionSectionChanges(): void {
    if (this.gridConnectionSection && !this.gridConnectionSectionInfo.isNew) {
      this.selectedGridProvider = this.gridProviders.find(gp => gp.id === this.gridConnectionSection.gridProviderId);
    }
    discardChanges(this.gridConnectionSectionInfo);
  }

  saveGridConnectionSectionChanges(entriesCollection: EntriesCollection): void {
    const updatedSection = getUpdatedGridConnectionSection(this.gridConnectionSection, this.selectedGridProvider, entriesCollection);
    saveChanges(this.projectId, updatedSection, this.gridConnectionSectionInfo,
      this.service.addGridConnectionSection.bind(this.service),
      this.service.updateGridConnectionSection.bind(this.service));
  }

  deleteGridConnectionSection(): void {
    remove(this.projectId, this.gridConnectionSectionInfo,
      this.service.deleteGridConnectionSection.bind(this.service));
  }

  initializeInstallationSection(elementId: string): void {
    this.installationSectionInfo.isNew = true;
    this.installationSectionInfo.data = {
      id: Guid.create(),
      createdOn: new Date(),
      supraregional: false,
      productRelatedEntries: [],
      customEntries: []
    };
    this.installationTable.startEdit();
    this.gotoElement(elementId);
  }

  supraregionalInstallationChecked(checked?: boolean): void {
    this.internalSupraregionalInstallation = checked ?? false;
    this.internalInstallationActivitiesFilter = checked
      ? () => true
      : (product: Product) => !product.name.startsWith('DE');
  }

  installationActivitiesFilterDiscarded(): void {
    this.supraregionalInstallationChecked(!this.supraregionalInstallation);
  }

  saveInstallationSectionChanges(entriesCollection: EntriesCollection): void {
    const updatedSection = {
      id: this.installationSectionInfo.data.id,
      createdOn: this.installationSectionInfo.data.createdOn,
      supraregional: this.supraregionalInstallation,
      ...entriesCollection
    };
    saveChanges(this.projectId, updatedSection, this.installationSectionInfo,
      this.service.addInstallationSection.bind(this.service),
      this.service.updateInstallationSection.bind(this.service));
  }

  discardInstallationSectionChanges(): void {
    discardChanges(this.installationSectionInfo);
    if (this.installationSection && !this.installationSectionInfo.isNew) {
      this.internalSupraregionalInstallation = this.installationSection.supraregional;
      this.supraregionalInstallationChecked(this.supraregionalInstallation)
    }
  }

  deleteInstallationSection(): void {
    remove(this.projectId, this.installationSectionInfo,
      this.service.deleteInstallationSection.bind(this.service));
  }


  initializeInstallationDescription(): void {
    this.installationDescriptionInfo.isNew = true;
    this.installationDescriptionInfo.data = {
      id: Guid.create(),
      text: getDefaultInstallationDescription()
    };
    this.installationDescriptionComponent.startEdit();
  }

  saveInstallationDescription(changedInstallationDescription: string): void {
    const updatedSection = {
      id: this.installationDescriptionInfo.data.id,
      text: changedInstallationDescription
    };
    saveChanges(this.projectId, updatedSection, this.installationDescriptionInfo,
      this.service.addInstallationDescription.bind(this.service),
      this.service.updateInstallationDescription.bind(this.service));
  }

  discardInstallationDescriptionChanges(): void {
    discardChanges(this.installationDescriptionInfo);
  }

  deleteInstallationDescription(): void {
    remove(this.projectId, this.installationDescriptionInfo,
      this.service.deleteInstallationDescription.bind(this.service));
  }
}

function createEmptyGridConnectionSection(id: Guid, gridProvider: GridProvider, createdOn: Date): GridConnectionSection {
  return {
    id,
    gridProviderId: gridProvider.id,
    gridProviderName: gridProvider.name,
    createdOn,
    productRelatedEntries: [],
    customEntries: []
  };
}

function getUpdatedGridConnectionSection(originalSection: GridConnectionSection, gridProvider: GridProvider, newEnties: EntriesCollection)
  : GridConnectionSection {
  return {
    id: originalSection.id,
    gridProviderId: gridProvider.id,
    gridProviderName: gridProvider.name,
    createdOn: originalSection.createdOn,
    productRelatedEntries: newEnties.productRelatedEntries,
    customEntries: newEnties.customEntries
  };
}

function getDefaultInstallationDescription(): string
{
  return `Die am Standort für E-Mobilität zur Verfügung stehenden Parkplätze sollen mit Ladestationen ausgerüstet werden.

Dafür sind im ersten Ausbauschritt zwei Ladesäulen vom Typ „Fleet-BM01“ vorgesehen, die wie abgebildet aufgestellt werden können. Die Ladesäulen verfügen über je 2 Ladepunkte mit einer Leistung pro Ladepunkt von 3,7 bis 22 kW.

Im Angebot enthalten sind die Tiefbauarbeiten für Kabelgraben und Fundamente inkl. Verlegung, die Elektroinstallation, sowie Lieferung, Montage und Inbetriebnahme der Ladestation. Außerdem ist für den neuen Netzanschluss die Installation einer Zähleranschlusssäule (Maxi) mit Wandlermessung für eine separate Energiebelieferung eingeplant.



Netzanschluss/Versorgung aus Gebäude:

Die Versorgung der Ladestationen könnte aus dem öffentlichen Netz erfolgen. Die hierfür angegebenen Kosten sind reine Schätzkosten und können variieren. (Im Beispiel: Netzbetreiber Westfalen Weser Netz)



Abrechnungslösung "WW.Mobility.Marketplace":

Westfalen Weser betreibt die Ladepunkte über WW.Mobility.Marketplace, d.h. es kann an der Ladesäule mit über 20 verschiedenen Fahrstromanbietern geladen werden, der entnommene Fahrstrom wird von Westfalen Weser erstattet. Alle aktuell angebundenen Fahrstromanbieter finden Sie unter: www.westfalenweser.com/ladezugang

Weiterhin kann der Nutzer einen Ladevorgang direkt mit einer Girokarte mit NFC Funktion über den eingebauten Girokartenleser starten, die Kosten werden per Lastschrift abgerechnet.



Portalzugang für den Betreiber:

Über ein Portalzugang legen Sie den Ladepreis über Girokarte fest und können diesen jederzeit anpassen.`;
}
